import { useState, useEffect, useRef, useMemo } from 'react';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { MasterFilters, MasterSummaryCards, MasterStatsRow, MasterToggle } from '@/pages/masters/common/commonComponents';
import {
    createMachine,
    getAllMachines,
    updateMachine,
    deleteMachine,
    getNextMachineCode,
    type MachineResponse
} from '@/api/machine.api';
import { getAllWorkCenters } from '@/api/workcenter.api';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { formatWorkCenter } from '@/hooks/useWorkCenters';
import AssignOperatorsModal from './components/AssignOperatorsModal';
import { getAllOperators, type OperatorResponse } from '@/api/operators.api';
import SearchableMultiSelect, { type MultiSelectOption } from '@/components/base/SearchableMultiSelect';

type MachineStatus = 'RUNNING' | 'IDLE' | 'MAINTENANCE' | 'BREAKDOWN';

interface Machine {
    id: string;
    name: string;
    model: string;
    workCenterId: string;
    workCenterName?: string | null;
    capacityPerHour: number;
    status: MachineStatus;
    lastMaintenanceDate: string | null;
    maintenanceFrequencyDays: number | null;
    isActive: boolean;
    warehouseId: string | null;
    warehouseName?: string | null;
    machineCode: string;
    make: string;
    serialNumber: string;
    installationDate: string | null;
    warrantyExpiryDate: string | null;
    machineHourRate: number | null;
    assignedOperatorIds?: string[];
}

interface WorkCenterOption {
    id: string;
    code: string;
    /** From the work center's type (Machine, Both, or a custom type marked "machine required") */
    machineRequired: boolean;
    name: string;
    type: string;
    isActive: boolean;
    warehouseId?: string | null;
}

interface MachineForm {
    name: string;
    model: string;
    workCenterId: string;
    capacityPerHour: string;
    status: MachineStatus;
    lastMaintenanceDate: string;
    maintenanceFrequencyDays: string;
    isActive: boolean;
    warehouseId: string;
    warehouseName?: string | null;
    machineCode: string;
    make: string;
    serialNumber: string;
    installationDate: string;
    warrantyExpiryDate: string;
    machineHourRate: string;
    operatorIds: string[];
}

const STATUS_OPTIONS: { value: MachineStatus; label: string }[] = [
    { value: 'RUNNING', label: 'Running' },
    { value: 'IDLE', label: 'Idle' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'BREAKDOWN', label: 'Breakdown' },
];

const STATUS_BADGE: Record<MachineStatus, { label: string; cls: string }> = {
    RUNNING: { label: 'Running', cls: 'bg-green-50 text-green-700 border-green-200' },
    IDLE: { label: 'Idle', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    MAINTENANCE: { label: 'Maintenance', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    BREAKDOWN: { label: 'Breakdown', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const emptyForm: MachineForm = {
    name: '', model: '', workCenterId: '', capacityPerHour: '', status: 'IDLE',
    lastMaintenanceDate: '', maintenanceFrequencyDays: '', isActive: true, warehouseId: '', warehouseName: '',
    machineCode: '', make: '', serialNumber: '', installationDate: '', warrantyExpiryDate: '', machineHourRate: '',
    operatorIds: [],
};

/** The server's message for a failed request (e.g. "Machine with this name already exists"), else the error's own. */
const apiErrorMessage = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    || (err instanceof Error ? err.message : fallback);

const SKILL_LEVEL_BADGE: Record<string, string> = {
    TRAINEE: 'bg-slate-50 text-slate-600 border-slate-200',
    SKILLED: 'bg-blue-50 text-blue-700 border-blue-200',
    EXPERT: 'bg-green-50 text-green-700 border-green-200',
};

const titleCase = (v: string | null | undefined) =>
    String(v || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const WARRANTY_ALERT_DAYS = 30;

// Warranty state for the alert badge: expired, or expiring within WARRANTY_ALERT_DAYS
function warrantyStatus(m: Machine): { kind: 'EXPIRED' | 'EXPIRING'; days: number } | null {
    if (!m.warrantyExpiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(`${m.warrantyExpiryDate}T00:00:00`);
    const days = Math.round((expiry.getTime() - today.getTime()) / 86400000);
    if (days < 0) return { kind: 'EXPIRED', days };
    if (days <= WARRANTY_ALERT_DAYS) return { kind: 'EXPIRING', days };
    return null;
}

const inputCls = 'w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20';
const errorInputCls = 'w-full h-10 px-3 rounded-lg border border-red-400 text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 focus:ring-red-200';
const labelCls = 'text-xs font-semibold text-[#64748b] uppercase tracking-wide';

function isMaintenanceOverdue(m: Machine): boolean {
    if (!m.lastMaintenanceDate || !m.maintenanceFrequencyDays || m.maintenanceFrequencyDays <= 0) return false;
    const last = new Date(m.lastMaintenanceDate);
    const due = new Date(last);
    due.setDate(due.getDate() + m.maintenanceFrequencyDays);
    return new Date() > due;
}

// ─── Slide-Over Form ──────────────────────────────────────────────────────────
interface SlideOverProps {
    open: boolean;
    editing: Machine | null;
    onClose: () => void;
    onSave: (form: MachineForm) => Promise<void>;
    /** Warehouse the machine is saved in (header selection); its operators are offered */
    warehouseId: string | null;
}

function MachineSlideOver({ open, editing, onClose, onSave, warehouseId }: SlideOverProps) {
    const [form, setForm] = useState<MachineForm>({ ...emptyForm });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const firstInputRef = useRef<HTMLInputElement>(null);

    // Operators of the machine's warehouse, plus the ones with no warehouse set (all while none is chosen)
    const [operators, setOperators] = useState<OperatorResponse[]>([]);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const machineWarehouseId = editing?.warehouseId || warehouseId || undefined;
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const operatorsOf = (wh?: string) => getAllOperators(wh).then((r) => r.data ?? []).catch(() => [] as OperatorResponse[]);
        setLoadingOperators(true);
        Promise.all([
            operatorsOf(machineWarehouseId),
            machineWarehouseId ? operatorsOf().then((rows) => rows.filter((o) => !o.warehouse_id)) : Promise.resolve([] as OperatorResponse[]),
        ]).then(([own, shared]) => {
            if (cancelled) return;
            setOperators([...own, ...shared]);
            setLoadingOperators(false);
        });
        return () => { cancelled = true; };
    }, [open, machineWarehouseId]);

    const operatorOptions: MultiSelectOption[] = operators
        .filter((o) => o.is_active || form.operatorIds.includes(o.id))
        .map((o) => ({
            value: o.id,
            label: [o.employee_code, o.name].filter(Boolean).join(' · '),
            sublabel: [titleCase(o.skill), o.shift_name, !o.warehouse_id && machineWarehouseId ? 'No warehouse set' : '']
                .filter(Boolean).join(' · ') || undefined,
            badge: o.skill_level ? titleCase(o.skill_level) : undefined,
            isActive: o.is_active,
        }));
    const selectedOperators = form.operatorIds
        .map((id) => operators.find((o) => o.id === id))
        .filter((o): o is OperatorResponse => !!o);
    const removeOperator = (id: string) => setForm((p) => ({ ...p, operatorIds: p.operatorIds.filter((x) => x !== id) }));

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                name: editing?.name || '',
                model: editing?.model || '',
                workCenterId: editing?.workCenterId || '',
                capacityPerHour: editing?.capacityPerHour?.toString() || '',
                status: editing?.status || 'IDLE',
                lastMaintenanceDate: editing?.lastMaintenanceDate ?? '',
                maintenanceFrequencyDays: editing?.maintenanceFrequencyDays?.toString() || '',
                isActive: editing?.isActive ?? true,
                warehouseId: editing?.warehouseId || '',
                warehouseName: editing?.warehouseName || '',
                machineCode: editing?.machineCode || '',
                make: editing?.make || '',
                serialNumber: editing?.serialNumber || '',
                installationDate: editing?.installationDate || '',
                warrantyExpiryDate: editing?.warrantyExpiryDate || '',
                machineHourRate: editing?.machineHourRate != null ? String(editing.machineHourRate) : '',
                operatorIds: editing?.assignedOperatorIds ?? [],
            });
        } else {
            setForm({ ...emptyForm });
            // Suggest the next MC-xxx code; the user may overwrite it
            getNextMachineCode()
                .then((res) => {
                    const code = res.data?.machine_code;
                    if (code) setForm((p) => (p.machineCode ? p : { ...p, machineCode: code }));
                })
                .catch(() => { /* server assigns a code on save */ });
        }
        setErrors({});
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [open, editing]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const update = (field: keyof MachineForm, value: string | boolean) => {
        setForm((p) => ({ ...p, [field]: value }));
        if (errors[field]) setErrors((e) => { const n = { ...e }; delete n[field]; return n; });
    };

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.name.trim()) e.name = 'Machine name is required';
        if (form.machineCode.trim().length > 50) e.machineCode = 'Machine code can be at most 50 characters';
        if (!form.machineHourRate.trim()) {
            e.machineHourRate = 'Machine hour rate is required';
        } else if (!(Number(form.machineHourRate) >= 0)) {
            e.machineHourRate = 'Enter a valid rate (0 or more)';
        }
        if (form.installationDate && form.warrantyExpiryDate && form.warrantyExpiryDate < form.installationDate) {
            e.warrantyExpiryDate = 'Warranty expiry cannot be before installation date';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(form);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[600] flex justify-end">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-2xs transition-opacity" onClick={onClose} />
            <div className="relative bg-white w-full max-w-xl flex flex-col h-full shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] bg-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#4f46e5] text-lg shrink-0 shadow-2xs">
                            <i className="ri-settings-3-line" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-[#1e293b]">
                                {editing ? 'Edit Machine' : 'Add Machine'}
                            </h3>
                            <p className="text-xs text-[#64748b] mt-0.5">
                                {editing ? 'Update machine specifications and settings' : 'Register a new machine in master database'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] hover:text-[#1e293b] transition-colors cursor-pointer"
                        title="Close"
                    >
                        <i className="ri-close-line text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-slate-50/30">
                    {/* Section 1: Identification */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-information-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Basic Information
                            </h4>
                        </div>

                        {/* Code + Name */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                            <div className="space-y-1.5 sm:col-span-1">
                                <div className="flex items-center justify-between">
                                    <label className={labelCls}>Code</label>
                                    <span className="text-[10px] text-slate-400 font-medium">Auto</span>
                                </div>
                                <input
                                    type="text"
                                    value={form.machineCode}
                                    onChange={(e) => update('machineCode', e.target.value.toUpperCase())}
                                    placeholder="MC-001"
                                    maxLength={50}
                                    className={`${errors.machineCode ? errorInputCls : inputCls} font-mono tracking-wider`}
                                />
                                {errors.machineCode && <p className="text-xs text-red-500">{errors.machineCode}</p>}
                            </div>

                            <div className="space-y-1.5 sm:col-span-2">
                                <label className={labelCls}>
                                    Machine Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    ref={firstInputRef}
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => update('name', e.target.value)}
                                    placeholder="e.g. CNC Milling Center 1"
                                    className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.name ? 'border-red-400 focus:ring-red-200' : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'}`}
                                />
                                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                            </div>
                        </div>

                        {/* Make + Model */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <div className="space-y-1.5">
                                <label className={labelCls}>Make / Manufacturer</label>
                                <input
                                    type="text"
                                    value={form.make}
                                    onChange={(e) => update('make', e.target.value)}
                                    placeholder="e.g. Haas, BFW, Ace Micromatic"
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelCls}>Model Number</label>
                                <input
                                    type="text"
                                    value={form.model}
                                    onChange={(e) => update('model', e.target.value)}
                                    placeholder="e.g. VF-2, Jobber Elite"
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        {/* Serial Number */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <label className={labelCls}>Serial Number</label>
                                <span className="text-[10px] text-slate-400">For warranty & service claims</span>
                            </div>
                            <input
                                type="text"
                                value={form.serialNumber}
                                onChange={(e) => update('serialNumber', e.target.value)}
                                placeholder="e.g. SN-892401-2024"
                                className={`${inputCls} font-mono`}
                            />
                        </div>
                    </div>

                    {/* Section 2: Work Center & Capacity */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-building-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Capacity
                            </h4>
                        </div>

                        {/* Work center: machines are assigned from the Work Center screen */}
                        <p className="flex items-start gap-1.5 text-xs text-[#64748b]">
                            <i className="ri-information-line text-sm text-[#94a3b8]" />
                            {editing?.workCenterName
                                ? <span>Work center: <b className="font-semibold text-[#1e293b]">{editing.workCenterName}</b> — change it from Masters → Work Centers.</span>
                                : <span>Assign this machine to a work center from Masters → Work Centers.</span>}
                        </p>

                        {/* Capacity + Machine Hour Rate */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className={labelCls}>Capacity per Hour</label>
                                    <span className="text-[10px] text-slate-400">Units / hr</span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.capacityPerHour}
                                    onChange={(e) => update('capacityPerHour', e.target.value)}
                                    placeholder="e.g. 120"
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className={labelCls}>Machine Hour Rate (₹) <span className="text-red-500">*</span></label>
                                    <span className="text-[9px] text-slate-400">Running Cost/hr</span>
                                </div>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.machineHourRate}
                                    // No negative rates: block the minus / exponent keys and strip a pasted minus
                                    onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                                    onChange={(e) => update('machineHourRate', e.target.value.replace(/-/g, ''))}
                                    placeholder="e.g. 450.00"
                                    className={errors.machineHourRate ? errorInputCls : inputCls}
                                />
                                {errors.machineHourRate ? (
                                    <p className="text-xs text-red-500">{errors.machineHourRate}</p>
                                ) : editing && form.machineHourRate.trim() === '' ? (
                                    // An older machine saved without a rate: say so before the save asks for it
                                    <p className="flex items-start gap-1 text-xs text-amber-600">
                                        <i className="ri-error-warning-line mt-px" />
                                        This machine has no hour rate yet — production costing takes ₹0 for its hours until you enter one.
                                    </p>
                                ) : form.machineHourRate.trim() !== '' && Number(form.machineHourRate) === 0 && (
                                    // 0 is allowed, but every hour on this machine then costs nothing
                                    <p className="flex items-start gap-1 text-xs text-amber-600">
                                        <i className="ri-error-warning-line mt-px" />
                                        Machine cost will be ₹0 in production costing for every hour on this machine.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Operators */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-team-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Operators
                            </h4>
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelCls}>Assigned Operators</label>
                            <SearchableMultiSelect
                                value={form.operatorIds}
                                onChange={(ids) => setForm((p) => ({ ...p, operatorIds: ids }))}
                                options={operatorOptions}
                                placeholder={loadingOperators ? 'Loading operators…' : '— Select operators —'}
                                searchPlaceholder="Search by code, name or skill..."
                                emptyText={machineWarehouseId ? 'No operators in this warehouse — add them under Masters → Operators' : 'No operators yet — add them under Masters → Operators'}
                                noneSelectedText="No operator assigned to this machine"
                                icon="ri-user-settings-line"
                                direction="auto"
                                maxDisplayedChips={3}
                            />
                            <p className="text-xs text-[#94a3b8]">Only these operators are offered for this machine in work orders and production entries.</p>
                        </div>

                        {selectedOperators.length > 0 && (
                            <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] divide-y divide-[#e2e8f0] overflow-hidden">
                                {selectedOperators.map((o) => (
                                    <div key={o.id} className="flex items-center justify-between gap-2 px-3 py-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-[#1e293b] truncate">
                                                {o.employee_code && <span className="font-mono text-[#4f46e5] mr-1.5">{o.employee_code}</span>}
                                                {o.name}
                                            </p>
                                            <p className="text-xs text-[#94a3b8] truncate">
                                                {[titleCase(o.skill), o.shift_name ? `${o.shift_name} shift` : '', `₹${Number(o.wage_rate_per_hour) || 0}/hr`, o.phone]
                                                    .filter(Boolean).join(' · ')}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {o.skill_level && (
                                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${SKILL_LEVEL_BADGE[o.skill_level] || SKILL_LEVEL_BADGE.TRAINEE}`}>
                                                    {titleCase(o.skill_level)}
                                                </span>
                                            )}
                                            {!o.is_active && (
                                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">Inactive</span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => removeOperator(o.id)}
                                                title="Remove from this machine"
                                                className="w-6 h-6 flex items-center justify-center rounded text-[#94a3b8] hover:text-red-500 hover:bg-red-50 cursor-pointer"
                                            >
                                                <i className="ri-close-line" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Section 3: Status */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-pulse-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Operating Status <span className="text-red-500">*</span>
                            </h4>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {STATUS_OPTIONS.map((s) => {
                                const badge = STATUS_BADGE[s.value];
                                const isSelected = form.status === s.value;
                                return (
                                    <button
                                        key={s.value}
                                        type="button"
                                        onClick={() => update('status', s.value)}
                                        className={`flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                                            isSelected
                                                ? `${badge.cls} border-current ring-2 ring-current/20 shadow-xs font-bold`
                                                : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40 hover:text-[#4f46e5] bg-white'
                                        }`}
                                    >
                                        <span className={`w-2 h-2 rounded-full ${
                                            s.value === 'RUNNING' ? 'bg-green-500' :
                                            s.value === 'IDLE' ? 'bg-slate-400' :
                                            s.value === 'MAINTENANCE' ? 'bg-amber-500' : 'bg-red-500'
                                        }`} />
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-[#94a3b8]">
                            Status syncs automatically with Maintenance and Downtime logs, but can also be manually overridden.
                        </p>
                    </div>

                    {/* Section 4: Maintenance & Warranty */}
                    <div className="bg-white rounded-xl border border-slate-200/80 p-4 space-y-3.5 shadow-2xs">
                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                            <i className="ri-calendar-check-line text-[#4f46e5] text-sm" />
                            <h4 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
                                Maintenance & Warranty Schedule
                            </h4>
                        </div>

                        {/* Installation + Warranty */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <div className="space-y-1.5">
                                <label className={labelCls}>Installation Date</label>
                                <input
                                    type="date"
                                    value={form.installationDate}
                                    onChange={(e) => update('installationDate', e.target.value)}
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelCls}>Warranty Expiry Date</label>
                                <input
                                    type="date"
                                    value={form.warrantyExpiryDate}
                                    min={form.installationDate || undefined}
                                    onChange={(e) => update('warrantyExpiryDate', e.target.value)}
                                    className={errors.warrantyExpiryDate ? errorInputCls : inputCls}
                                />
                                {errors.warrantyExpiryDate && <p className="text-xs text-red-500">{errors.warrantyExpiryDate}</p>}
                            </div>
                        </div>

                        {/* Last Maintenance + Frequency - commented out as requested */}
                        {/*
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className={labelCls}>Last Maintenance</label>
                                    <span className="text-[10px] text-slate-400">Date</span>
                                </div>
                                <input
                                    type="date"
                                    value={form.lastMaintenanceDate}
                                    onChange={(e) => update('lastMaintenanceDate', e.target.value)}
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label className={labelCls}>Frequency</label>
                                    <span className="text-[10px] text-slate-400">Every X days</span>
                                </div>
                                <input
                                    type="number"
                                    min="1"
                                    value={form.maintenanceFrequencyDays}
                                    onChange={(e) => update('maintenanceFrequencyDays', e.target.value)}
                                    placeholder="e.g. 90"
                                    className={inputCls}
                                />
                            </div>
                        </div>
                        */}
                    </div>

                    {/* Section 5: Active Toggle */}
                    <div className="p-3 bg-white rounded-xl border border-slate-200/80 shadow-2xs">
                        <MasterToggle
                            checked={form.isActive}
                            onChange={(val) => update('isActive', val)}
                            activeLabel="Active — machine is operational and available for routing/work orders"
                            inactiveLabel="Inactive — machine is disabled and hidden from new assignments"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-white shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 h-9 px-5 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                    >
                        {isSaving ? (
                            <>
                                <i className="ri-loader-4-line animate-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line" /> {editing ? 'Update Machine' : 'Save Machine'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MachinesPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const [machines, setMachines] = useState<Machine[]>([]);
    const [workCenters, setWorkCenters] = useState<WorkCenterOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<MachineStatus | 'ALL'>('ALL');
    const [wcFilter, setWcFilter] = useState<string>('ALL');
    const [slideOver, setSlideOver] = useState<{ open: boolean; editing: Machine | null }>({ open: false, editing: null });
    const [deleteConfirm, setDeleteConfirm] = useState<Machine | null>(null);
    const [createdMachinePrompt, setCreatedMachinePrompt] = useState<Machine | null>(null);
    const [assignModal, setAssignModal] = useState<{ open: boolean; machine: Machine | null }>({ open: false, machine: null });

    const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();

    const mapApiToMachine = (m: MachineResponse): Machine => ({
        id: m.id,
        name: m.name,
        model: m.model || '',
        workCenterId: m.work_center_id || '',
        workCenterName: m.work_center_name || '',
        capacityPerHour: Number(m.capacity_per_hour || 0),
        status: m.status,
        lastMaintenanceDate: m.last_maintenance_date ? m.last_maintenance_date.split('T')[0] : null,
        maintenanceFrequencyDays: m.maintenance_frequency_days ? Number(m.maintenance_frequency_days) : null,
        isActive: m.is_active,
        warehouseId: m.warehouse_id || '',
        warehouseName: m.warehouse_name || '',
        machineCode: m.machine_code || '',
        make: m.make || '',
        serialNumber: m.serial_number || '',
        installationDate: m.installation_date ? m.installation_date.split('T')[0] : null,
        warrantyExpiryDate: m.warranty_expiry_date ? m.warranty_expiry_date.split('T')[0] : null,
        machineHourRate: m.machine_hour_rate != null && m.machine_hour_rate !== '' ? Number(m.machine_hour_rate) : null,
        assignedOperatorIds: Array.isArray(m.assigned_operator_ids) ? m.assigned_operator_ids : [],
    });

    const assetPayload = (form: MachineForm) => ({
        machineCode: form.machineCode.trim() || null,
        make: form.make.trim() || null,
        serialNumber: form.serialNumber.trim() || null,
        installationDate: form.installationDate || null,
        warrantyExpiryDate: form.warrantyExpiryDate || null,
        machineHourRate: form.machineHourRate !== '' ? Number(form.machineHourRate) : null,
        operatorIds: form.operatorIds,
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [mcRes, wcRes] = await Promise.all([
                getAllMachines(),
                getAllWorkCenters()
            ]);
            if (wcRes.success && wcRes.data) {
                setWorkCenters(
                    wcRes.data.map((wc) => ({
                        id: wc.id,
                        code: wc.code || '',
                        machineRequired: Boolean(wc.machine_required),
                        name: wc.name,
                        type: wc.type,
                        isActive: wc.is_active,
                        warehouseId: wc.warehouse_id || null,
                    }))
                );
            }
            if (mcRes.success && mcRes.data) {
                setMachines(mcRes.data.map(mapApiToMachine));
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const warehouseMachines = useMemo(() => {
        if (!selectedWarehouseId) return machines;
        return machines.filter((m) => m.warehouseId === selectedWarehouseId);
    }, [machines, selectedWarehouseId]);

    const filteredWorkCenters = useMemo(() => {
        let list = workCenters;
        if (selectedWarehouseId) {
            list = list.filter((wc) => wc.warehouseId === selectedWarehouseId);
        }
        return list;
    }, [workCenters, selectedWarehouseId]);

    const filtered = useMemo(() => {
        return warehouseMachines.filter((m) => {
            const q = search.toLowerCase();
            const matchSearch = !q || [m.name, m.model, m.machineCode, m.make, m.serialNumber]
                .some((v) => (v || '').toLowerCase().includes(q));
            const matchStatus = statusFilter === 'ALL' || m.status === statusFilter;
            const matchWc = wcFilter === 'ALL' || m.workCenterId === wcFilter;
            return matchSearch && matchStatus && matchWc;
        });
    }, [warehouseMachines, search, statusFilter, wcFilter]);

    const openAdd = () => setSlideOver({ open: true, editing: null });
    const openEdit = (m: Machine) => setSlideOver({ open: true, editing: m });
    const closeSlideOver = () => setSlideOver({ open: false, editing: null });

    const handleSave = async (form: MachineForm) => {
        try {
            if (slideOver.editing) {
                const res = await updateMachine(slideOver.editing.id, {
                    name: form.name,
                    model: form.model || null,
                    capacityPerHour: form.capacityPerHour ? Number(form.capacityPerHour) : null,
                    status: form.status,
                    lastMaintenanceDate: form.lastMaintenanceDate || null,
                    maintenanceFrequencyDays: form.maintenanceFrequencyDays ? Number(form.maintenanceFrequencyDays) : null,
                    isActive: form.isActive,
                    warehouseId: selectedWarehouseId || null,
                    ...assetPayload(form),
                });
                if (res.success && res.data) {
                    setMachines((prev) =>
                        prev.map((m) =>
                            m.id === slideOver.editing!.id
                                ? mapApiToMachine(res.data!)
                                : m,
                        ),
                    );
                    toast.success('Machine updated successfully');
                }
            } else {
                const res = await createMachine({
                    name: form.name,
                    model: form.model || null,
                    capacityPerHour: form.capacityPerHour ? Number(form.capacityPerHour) : null,
                    status: form.status,
                    lastMaintenanceDate: form.lastMaintenanceDate || null,
                    maintenanceFrequencyDays: form.maintenanceFrequencyDays ? Number(form.maintenanceFrequencyDays) : null,
                    isActive: form.isActive,
                    warehouseId: selectedWarehouseId || null,
                    ...assetPayload(form),
                });
                if (res.success && res.data) {
                    const newMachine = mapApiToMachine(res.data!);
                    setMachines((prev) => [newMachine, ...prev]);
                    toast.success('Machine created successfully');
                    setCreatedMachinePrompt(newMachine);
                }
            }
            closeSlideOver();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Failed to save machine'));
            throw err;
        }
    };

    const handleToggleActive = async (m: Machine) => {
        // Optimistic update
        setMachines((prev) => prev.map((x) => x.id === m.id ? { ...x, isActive: !x.isActive } : x));
        try {
            await updateMachine(m.id, { isActive: !m.isActive });
            toast.success(`${m.name} ${m.isActive ? 'deactivated' : 'activated'}`);
        } catch (err) {
            // Rollback
            setMachines((prev) => prev.map((x) => x.id === m.id ? { ...x, isActive: m.isActive } : x));
            toast.error(apiErrorMessage(err, 'Failed to update status'));
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        try {
            await deleteMachine(deleteConfirm.id);
            setMachines((prev) => prev.filter((m) => m.id !== deleteConfirm.id));
            toast.success(`"${deleteConfirm.name}" removed`);
            setDeleteConfirm(null);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Failed to delete machine'));
        }
    };

    const getWorkCenterName = (id: string) => {
        const wc = workCenters.find((w) => w.id === id);
        return wc ? formatWorkCenter(wc.code, wc.name) : id;
    };

    const getWorkCenterType = (id: string): string | null => {
        const wc = workCenters.find((w) => w.id === id);
        return wc ? wc.type : null;
    };

    // Status options for SearchableSelect
    const statusOptions: SearchableOption[] = useMemo(() => [
        { value: 'ALL', label: 'All Status' },
        ...STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
    ], []);

    const wcFilterOptions: SearchableOption[] = useMemo(() => [
        { value: 'ALL', label: 'All Work Centers' },
        ...filteredWorkCenters.filter((wc) => wc.isActive).map((wc) => ({
            value: wc.id,
            label: formatWorkCenter(wc.code, wc.name),
            badge: wc.type ? (wc.type.toUpperCase() === 'BOTH' ? 'Machine + Labor' : wc.type) : undefined,
        })),
    ], [filteredWorkCenters]);

    // KPI counts
    const totalCount = warehouseMachines.length;
    const runningCount = warehouseMachines.filter((m) => m.status === 'RUNNING').length;
    const idleCount = warehouseMachines.filter((m) => m.status === 'IDLE').length;
    const maintenanceCount = warehouseMachines.filter((m) => m.status === 'MAINTENANCE').length;
    const breakdownCount = warehouseMachines.filter((m) => m.status === 'BREAKDOWN').length;
    const needsMaintCount = warehouseMachines.filter((m) => isMaintenanceOverdue(m)).length;
    const hasActiveFilters = Boolean(search || statusFilter !== 'ALL' || wcFilter !== 'ALL');

    return (
        <AppLayout>
            <div className="p-6 space-y-5 w-full max-w-[1600px] mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer shadow-2xs"
                            title="Go back"
                        >
                            <i className="ri-arrow-left-line text-base" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-[#1e293b]">Machines</h1>
                                {selectedWarehouseName && (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                        <i className="ri-store-2-line text-xs" />
                                        {selectedWarehouseName}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-[#64748b] mt-0.5">Manage machinery, operating statuses, and work center assignments</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearch('');
                                    setStatusFilter('ALL');
                                    setWcFilter('ALL');
                                }}
                                className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-1.5"
                            >
                                <i className="ri-filter-off-line text-sm" />
                                Clear Filters
                            </button>
                        )}
                        <button
                            onClick={openAdd}
                            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap shadow-xs"
                        >
                            <i className="ri-add-line text-base" /> Add Machine
                        </button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <button
                        type="button"
                        onClick={() => setStatusFilter('ALL')}
                        className={`bg-white border rounded-xl p-3 text-left transition-all cursor-pointer ${
                            statusFilter === 'ALL'
                                ? 'border-[#4f46e5] ring-2 ring-indigo-100 shadow-xs'
                                : 'border-[#e2e8f0] hover:border-[#4f46e5]/40'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">Total</span>
                            <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center text-[#4f46e5]">
                                <i className="ri-settings-3-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{totalCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">All Machines</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter(statusFilter === 'RUNNING' ? 'ALL' : 'RUNNING')}
                        className={`bg-white border rounded-xl p-3 text-left transition-all cursor-pointer ${
                            statusFilter === 'RUNNING'
                                ? 'border-green-500 ring-2 ring-green-100 shadow-xs'
                                : 'border-[#e2e8f0] hover:border-green-300'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-green-700">Running</span>
                            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
                                <i className="ri-play-circle-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{runningCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">In Production</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter(statusFilter === 'IDLE' ? 'ALL' : 'IDLE')}
                        className={`bg-white border rounded-xl p-3 text-left transition-all cursor-pointer ${
                            statusFilter === 'IDLE'
                                ? 'border-slate-500 ring-2 ring-slate-100 shadow-xs'
                                : 'border-[#e2e8f0] hover:border-slate-300'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Idle</span>
                            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                                <i className="ri-pause-circle-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{idleCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">Ready for work</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter(statusFilter === 'MAINTENANCE' ? 'ALL' : 'MAINTENANCE')}
                        className={`bg-white border rounded-xl p-3 text-left transition-all cursor-pointer ${
                            statusFilter === 'MAINTENANCE'
                                ? 'border-amber-500 ring-2 ring-amber-100 shadow-xs'
                                : 'border-[#e2e8f0] hover:border-amber-300'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Maintenance</span>
                            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
                                <i className="ri-tools-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{maintenanceCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">Under Service</p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setStatusFilter(statusFilter === 'BREAKDOWN' ? 'ALL' : 'BREAKDOWN')}
                        className={`bg-white border rounded-xl p-3 text-left transition-all cursor-pointer ${
                            statusFilter === 'BREAKDOWN'
                                ? 'border-rose-500 ring-2 ring-rose-100 shadow-xs'
                                : 'border-[#e2e8f0] hover:border-rose-300'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Breakdown</span>
                            <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
                                <i className="ri-alarm-warning-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{breakdownCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">Needs Repair</p>
                    </button>

                    <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 text-left">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-orange-700">Due</span>
                            <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
                                <i className="ri-alert-line text-sm" />
                            </div>
                        </div>
                        <p className="text-xl font-bold text-[#1e293b] mt-1">{needsMaintCount}</p>
                        <p className="text-[11px] text-[#94a3b8] mt-0.5">PM Overdue</p>
                    </div>
                </div>

                {/* Filter Bar */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-2xs">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[240px]">
                            <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                                Search
                            </label>
                            <div className="relative">
                                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search machines, model, code, make..."
                                    className="w-full h-10 pl-9 pr-8 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                                    >
                                        <i className="ri-close-line text-base" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="w-[180px]">
                            <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                                Status
                            </label>
                            <SearchableSelect
                                value={statusFilter}
                                onChange={(val) => setStatusFilter(val as MachineStatus | 'ALL')}
                                options={statusOptions}
                                placeholder="All Status"
                                searchPlaceholder="Search status..."
                            />
                        </div>

                        <div className="w-[220px]">
                            <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                                Work Center
                            </label>
                            <SearchableSelect
                                value={wcFilter}
                                onChange={setWcFilter}
                                options={wcFilterOptions}
                                placeholder="All Work Centers"
                                searchPlaceholder="Search work center..."
                            />
                        </div>

                        <div className="self-end pb-1 text-xs font-medium text-[#64748b] ml-auto">
                            Showing <span className="font-bold text-[#1e293b]">{filtered.length}</span> of {warehouseMachines.length} machines
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-2xs">
                    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)]">
                        <table className="w-full min-w-[860px] text-sm border-collapse">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                    {['Machine Name', 'Model', 'Work Center', 'Capacity/hr', 'Status', 'Last Maintenance', 'Actions'].map((h, i) => (
                                        <th
                                            key={h}
                                            className={`px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap ${
                                                i === 6 ? 'text-right' : 'text-left'
                                            }`}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#f1f5f9]">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <i className="ri-loader-4-line text-3xl text-[#4f46e5] animate-spin block mb-2" />
                                            <p className="text-[#64748b] text-sm font-medium">Loading machines...</p>
                                        </td>
                                    </tr>
                                ) : filtered.map((m, idx) => {
                                    const badge = STATUS_BADGE[m.status] || STATUS_BADGE.IDLE;
                                    const overdue = isMaintenanceOverdue(m);
                                    const warranty = warrantyStatus(m);
                                    const wcType = getWorkCenterType(m.workCenterId);
                                    return (
                                        <tr key={m.id} className={`hover:bg-[#f8fafc] transition-colors ${idx % 2 !== 0 ? 'bg-[#fcfcfd]' : ''}`}>
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${badge.cls} border shrink-0`}>
                                                        <i className="ri-settings-3-line text-base" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-[#1e293b] whitespace-nowrap">{m.name}</p>
                                                            {!m.isActive && (
                                                                <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                                                    Inactive
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-xs text-[#94a3b8] whitespace-nowrap mt-0.5">
                                                            {m.machineCode && (
                                                                <span className="font-mono font-semibold text-[#4f46e5] bg-indigo-50 border border-indigo-100 px-1.5 py-0.2 rounded text-[11px]">
                                                                    {m.machineCode}
                                                                </span>
                                                            )}
                                                            {m.make && <span>{m.make}</span>}
                                                            {m.serialNumber && (
                                                                <span className="text-slate-400 font-mono text-[10px]">
                                                                    (S/N: {m.serialNumber})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 text-sm text-[#64748b] whitespace-nowrap">
                                                {m.model || <span className="text-[#94a3b8] italic">—</span>}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-sm font-medium text-[#1e293b]">{getWorkCenterName(m.workCenterId)}</span>
                                                    {wcType && (
                                                        <span
                                                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                                                                wcType === 'MACHINE'
                                                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                                    : wcType === 'LABOR'
                                                                    ? 'bg-green-50 text-green-700 border-green-200'
                                                                    : 'bg-purple-50 text-purple-700 border-purple-200'
                                                            }`}
                                                            title={wcType === 'MACHINE' ? 'Machine Center' : wcType === 'LABOR' ? 'Labor Center' : 'Combined Center'}
                                                        >
                                                            {wcType === 'MACHINE' ? 'Machine' : wcType === 'LABOR' ? 'Labor' : 'Both'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 text-[#1e293b] whitespace-nowrap">
                                                {m.capacityPerHour ? (
                                                    <span className="font-semibold text-slate-800">{m.capacityPerHour} <span className="font-normal text-xs text-slate-500">units/hr</span></span>
                                                ) : (
                                                    <span className="text-[#94a3b8] italic font-normal">—</span>
                                                )}
                                                {m.machineHourRate != null && m.machineHourRate > 0 && (
                                                    <p className="text-[11px] text-[#64748b] mt-0.5 font-mono">₹{m.machineHourRate}/hr</p>
                                                )}
                                                {/* Older machines can have no rate: costing then takes ₹0 for its hours */}
                                                {m.machineHourRate == null && (
                                                    <p className="flex items-center gap-1 text-[11px] text-amber-600 mt-0.5" title="Open the machine and enter its hour rate (0 if it has no running cost)">
                                                        <i className="ri-error-warning-line" /> Hour rate not set
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex flex-col gap-1 items-start">
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badge.cls}`}>
                                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                                        {badge.label}
                                                    </span>
                                                    {overdue && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                                            <i className="ri-alert-line text-[10px]" />
                                                            PM Overdue
                                                        </span>
                                                    )}
                                                    {warranty && (
                                                        <span
                                                            title={`Warranty expiry: ${m.warrantyExpiryDate}`}
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"
                                                        >
                                                            <i className="ri-shield-line text-[10px]" />
                                                            {warranty.kind === 'EXPIRED'
                                                                ? 'Warranty Expired'
                                                                : warranty.days === 0
                                                                ? 'Warranty ends today'
                                                                : `Warranty ends in ${warranty.days}d`}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                {m.lastMaintenanceDate ? (
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-medium text-[#1e293b]">{m.lastMaintenanceDate}</span>
                                                        {m.maintenanceFrequencyDays && (
                                                            <span className="text-[10px] text-[#94a3b8]">Every {m.maintenanceFrequencyDays} days</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-[#94a3b8] italic">Not recorded</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => setAssignModal({ open: true, machine: m })}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-slate-500 hover:text-[#4f46e5] transition-colors cursor-pointer relative"
                                                        title={`Assign Operators${m.assignedOperatorIds && m.assignedOperatorIds.length > 0 ? ` (${m.assignedOperatorIds.length} assigned)` : ''}`}
                                                    >
                                                        <i className="ri-user-settings-line text-sm" />
                                                        {m.assignedOperatorIds && m.assignedOperatorIds.length > 0 && (
                                                            <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-0.5 bg-[#4f46e5] text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-2xs">
                                                                {m.assignedOperatorIds.length}
                                                            </span>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => openEdit(m)}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-slate-500 hover:text-[#4f46e5] transition-colors cursor-pointer"
                                                        title="Edit Machine"
                                                    >
                                                        <i className="ri-edit-line text-sm" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm(m)}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-500 transition-colors cursor-pointer"
                                                        title="Delete Machine"
                                                    >
                                                        <i className="ri-delete-bin-line text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!isLoading && filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-3 text-slate-400">
                                                <i className="ri-settings-3-line text-2xl" />
                                            </div>
                                            <p className="text-[#1e293b] text-base font-semibold">No machines found</p>
                                            <p className="text-[#64748b] text-xs mt-1">
                                                {hasActiveFilters
                                                    ? 'No machines match the selected filter criteria. Try adjusting or clearing your filters.'
                                                    : 'No machines registered yet. Click "Add Machine" to register your first machine.'}
                                            </p>
                                            {hasActiveFilters ? (
                                                <button
                                                    onClick={() => {
                                                        setSearch('');
                                                        setStatusFilter('ALL');
                                                        setWcFilter('ALL');
                                                    }}
                                                    className="mt-3 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-indigo-50 text-[#4f46e5] border border-indigo-200 hover:bg-indigo-100 transition-colors cursor-pointer"
                                                >
                                                    Clear Filters
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={openAdd}
                                                    className="mt-3 px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-[#4f46e5] text-white hover:bg-indigo-700 transition-colors cursor-pointer"
                                                >
                                                    Add First Machine
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <MachineSlideOver
                open={slideOver.open}
                editing={slideOver.editing}
                onClose={closeSlideOver}
                onSave={handleSave}
                warehouseId={selectedWarehouseId}
            />

            <ConfirmDialog
                open={!!deleteConfirm}
                title="Remove Machine"
                message={`Remove "${deleteConfirm?.name}"? This action cannot be undone.`}
                variant="danger"
                confirmLabel="Yes, Remove"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteConfirm(null)}
            />

            <AssignOperatorsModal
                open={assignModal.open}
                machine={assignModal.machine}
                onClose={() => setAssignModal({ open: false, machine: null })}
                onSuccess={(updated) => {
                    setMachines((prev) =>
                        prev.map((item) =>
                            item.id === updated.id
                                ? {
                                      ...item,
                                      assignedOperatorIds: Array.isArray(updated.assigned_operator_ids)
                                          ? updated.assigned_operator_ids
                                          : [],
                                  }
                                : item
                        )
                    );
                }}
            />

            {/* ═══ CREATE SCHEDULE PROMPT MODAL ═══ */}
            {createdMachinePrompt && (
                <div className="fixed inset-0 z-[700] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" onClick={() => setCreatedMachinePrompt(null)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center z-10 border border-slate-100">
                        <div className="w-14 h-14 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                            <i className="ri-settings-3-line text-2xl text-indigo-600" />
                        </div>
                        <h3 className="text-lg font-bold text-[#1e293b] mb-1">
                            Machine Created Successfully! 🎉
                        </h3>
                        <p className="text-sm font-semibold text-indigo-600 mb-2">
                            {createdMachinePrompt.name} {createdMachinePrompt.model ? `(${createdMachinePrompt.model})` : ''}
                        </p>
                        <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                            Would you like to set up a Preventive Maintenance Schedule for this machine now?
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => setCreatedMachinePrompt(null)}
                                className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold text-xs transition-colors cursor-pointer"
                            >
                                Skip for Now
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const m = createdMachinePrompt;
                                    setCreatedMachinePrompt(null);
                                    navigate('/manufacturing/maintenance/schedules/new', {
                                        state: { machine: { id: m.id, name: m.name } }
                                    });
                                }}
                                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#4f46e5] hover:bg-indigo-700 text-white font-semibold text-xs flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer"
                            >
                                <i className="ri-calendar-check-line text-base" />
                                Create Maintenance Schedule
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppLayout>
    );
}